/**
 * Hook for managing dashboard layout persistence in localStorage
 * Handles both legacy (string[]) and new (SavedWidgetData[]) formats
 */

import { useCallback } from 'react'
import { WidgetConfig, DashboardWidget, SavedWidgetData } from '../types'

interface UseLayoutStorageProps {
    storageKey: string
    initialWidgets: WidgetConfig[]
}

interface UseLayoutStorageReturn {
    loadSavedLayout: () => DashboardWidget[]
    saveLayout: (widgets: DashboardWidget[]) => void
}

export function useLayoutStorage({
    storageKey,
    initialWidgets,
}: UseLayoutStorageProps): UseLayoutStorageReturn {
    const loadSavedLayout = useCallback((): DashboardWidget[] => {
        try {
            const saved = localStorage.getItem(storageKey)
            if (saved) {
                const parsedData = JSON.parse(saved) as
                    | SavedWidgetData[]
                    | string[]

                // Handle both old format (string[]) and new format (SavedWidgetData[])
                const isNewFormat =
                    parsedData.length > 0 && typeof parsedData[0] === 'object'

                const widgetMap = new Map(initialWidgets.map((w) => [w.id, w]))
                const orderedWidgets: DashboardWidget[] = []

                if (isNewFormat) {
                    // New format with colSpan and isVisible
                    ;(parsedData as SavedWidgetData[]).forEach(
                        (data, index) => {
                            const widget = widgetMap.get(data.id)
                            if (widget) {
                                orderedWidgets.push({
                                    ...widget,
                                    colSpan: data.colSpan || widget.colSpan,
                                    order: index,
                                    isVisible: data.isVisible !== false, // default true if not set
                                })
                                widgetMap.delete(data.id)
                            }
                        }
                    )
                } else {
                    // Old format (just IDs)
                    ;(parsedData as string[]).forEach((id, index) => {
                        const widget = widgetMap.get(id)
                        if (widget) {
                            orderedWidgets.push({
                                ...widget,
                                order: index,
                                isVisible: true,
                            })
                            widgetMap.delete(id)
                        }
                    })
                }

                // Add any new widgets not in saved order
                widgetMap.forEach((widget) => {
                    orderedWidgets.push({
                        ...widget,
                        order: orderedWidgets.length,
                        isVisible: true,
                    })
                })

                return orderedWidgets
            }
        } catch (error) {
            console.warn('Failed to load dashboard layout:', error)
        }
        return initialWidgets.map((w, index) => ({
            ...w,
            order: index,
            isVisible: true,
        }))
    }, [storageKey, initialWidgets])

    const saveLayout = useCallback(
        (widgetsToSave: DashboardWidget[]) => {
            try {
                const data: SavedWidgetData[] = widgetsToSave
                    .sort((a, b) => a.order - b.order)
                    .map((w) => ({
                        id: w.id,
                        colSpan: w.colSpan,
                        isVisible: w.isVisible,
                    }))
                localStorage.setItem(storageKey, JSON.stringify(data))
            } catch (error) {
                console.warn('Failed to save dashboard layout:', error)
            }
        },
        [storageKey]
    )

    return {
        loadSavedLayout,
        saveLayout,
    }
}
